<script setup>
/**
 * RED-310 — "Asukoht" zone of the analytics form: Haldusüksus (three
 * cascading MultiSelects fed by the options tree) or Nähtav kaardiala (the
 * map's current EPSG:3301 bbox, read live from the transaction store). A
 * company switched to the REdata pool (RED-322) reads every company's deals,
 * so the "no location" label and the map hint say so.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEt } from '@/services/numberFormat'
import { useAnalyticsStore } from '@/stores/analyticsStore'

const { t } = useI18n()
const store = useAnalyticsStore()

const modeOptions = computed(() => [
  { value: 'units', label: t('messages.transactions.analytics.form.location_units') },
  { value: 'bbox', label: t('messages.transactions.analytics.form.location_bbox') },
])

const bboxSize = computed(() => {
  const size = store.mapBboxSizeKm
  if (!size) return null
  return t('messages.transactions.analytics.form.bbox_size', {
    w: formatEt(size.width, 1),
    h: formatEt(size.height, 1),
  })
})

const onMaakondChange = () => store.pruneChildren()
const onOmavalitsusChange = () => store.pruneChildren()
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-gray-700">
        {{ $t('messages.transactions.analytics.form.location') }}
      </label>
      <SelectButton
        v-model="store.form.locationMode"
        :options="modeOptions"
        option-label="label"
        option-value="value"
        :allow-empty="false"
        size="small"
        class="w-auto!"
        data-testid="txn-analytics-location-mode"
      />
    </div>

    <template v-if="store.form.locationMode === 'units'">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div class="flex flex-col gap-1">
          <label for="txn-analytics-maakond" class="text-sm font-medium text-gray-700">
            {{ $t('messages.transactions.analytics.form.maakond') }}
          </label>
          <MultiSelect
            v-model="store.form.maakond"
            input-id="txn-analytics-maakond"
            :options="store.maakondOptions"
            option-label="label"
            data-key="key"
            display="chip"
            show-clear
            filter
            :loading="store.optionsLoading"
            :placeholder="$t('messages.transactions.analytics.form.all')"
            data-testid="txn-analytics-maakond"
            @update:model-value="onMaakondChange"
          >
            <template #option="{ option }">
              <span class="flex w-full items-center justify-between gap-3">
                <span>{{ option.label }}</span>
                <span class="text-xs text-gray-500 tabular-nums">{{ formatEt(option.n, 0) }}</span>
              </span>
            </template>
          </MultiSelect>
        </div>

        <div class="flex flex-col gap-1">
          <label for="txn-analytics-omavalitsus" class="text-sm font-medium text-gray-700">
            {{ $t('messages.transactions.analytics.form.omavalitsus') }}
          </label>
          <MultiSelect
            v-model="store.form.omavalitsus"
            input-id="txn-analytics-omavalitsus"
            :options="store.omavalitsusOptions"
            option-label="label"
            option-group-label="label"
            option-group-children="items"
            data-key="key"
            display="chip"
            show-clear
            filter
            :disabled="!store.form.maakond.length"
            :placeholder="$t('messages.transactions.analytics.form.all')"
            data-testid="txn-analytics-omavalitsus"
            @update:model-value="onOmavalitsusChange"
          >
            <template #option="{ option }">
              <span class="flex w-full items-center justify-between gap-3">
                <span>{{ option.label }}</span>
                <span class="text-xs text-gray-500 tabular-nums">{{ formatEt(option.n, 0) }}</span>
              </span>
            </template>
          </MultiSelect>
        </div>

        <div class="flex flex-col gap-1">
          <label for="txn-analytics-asum" class="text-sm font-medium text-gray-700">
            {{ $t('messages.transactions.analytics.form.asum') }}
          </label>
          <MultiSelect
            v-model="store.form.asum"
            input-id="txn-analytics-asum"
            :options="store.asumOptions"
            option-label="label"
            option-group-label="label"
            option-group-children="items"
            data-key="key"
            display="chip"
            show-clear
            filter
            :disabled="!store.hasAsumOptions"
            :placeholder="$t('messages.transactions.analytics.form.all')"
            data-testid="txn-analytics-asum"
          >
            <template #option="{ option }">
              <span
                class="flex w-full items-center justify-between gap-3"
                :class="{ 'pl-4': option.parent }"
              >
                <span>{{ option.label }}</span>
                <span class="text-xs text-gray-500 tabular-nums">{{ formatEt(option.n, 0) }}</span>
              </span>
            </template>
          </MultiSelect>
        </div>
      </div>

      <p class="text-sm text-gray-600 min-h-5" data-testid="txn-analytics-location-summary">
        {{ store.locationSummary ?? $t(store.isRedataPool ? 'messages.transactions.analytics.form.location_all_redata' : 'messages.transactions.analytics.form.location_all') }}
      </p>
    </template>

    <template v-else>
      <Message severity="info" :closable="false" size="small">
        <span data-testid="txn-analytics-bbox-hint">{{ $t(store.isRedataPool ? 'messages.transactions.analytics.form.bbox_hint_redata' : 'messages.transactions.analytics.form.bbox_hint') }}</span>
        <span v-if="bboxSize" class="ml-1 text-gray-600" data-testid="txn-analytics-bbox-size">{{ bboxSize }}</span>
        <span v-else class="ml-1 text-gray-600">{{ $t('messages.transactions.analytics.form.bbox_missing') }}</span>
      </Message>
    </template>
  </div>
</template>
